import * as fabric from 'fabric';

export class ViewportCulling {
  private canvas: fabric.Canvas;
  private cullingEnabled: boolean = true;
  private visibilityCache: Map<string, boolean> = new Map();
  private viewportPadding: number = 100; // Pixels to render outside viewport
  
  constructor(canvas: fabric.Canvas) {
    this.canvas = canvas;
    this.setupViewportCulling();
  }
  
  private setupViewportCulling() {
    // Override renderAll to include culling
    const originalRenderAll = this.canvas.renderAll.bind(this.canvas);
    
    this.canvas.renderAll = () => {
      if (this.cullingEnabled) {
        this.updateObjectVisibility();
      }
      return originalRenderAll();
    };
    
    // Update visibility on canvas change
    // We'll hook into the render cycle instead
  }
  
  private updateObjectVisibility() {
    const vpt = this.canvas.viewportTransform!;
    const zoom = this.canvas.getZoom();
    
    // Calculate viewport bounds
    const viewportTL = fabric.util.transformPoint(
      { x: 0, y: 0 },
      fabric.util.invertTransform(vpt)
    );
    
    const viewportBR = fabric.util.transformPoint(
      { x: this.canvas.getWidth(), y: this.canvas.getHeight() },
      fabric.util.invertTransform(vpt)
    );
    
    // Expand bounds by padding
    const padding = this.viewportPadding / zoom;
    const bounds = {
      left: viewportTL.x - padding,
      top: viewportTL.y - padding,
      right: viewportBR.x + padding,
      bottom: viewportBR.y + padding
    };
    
    // Check each object
    this.canvas.getObjects().forEach(obj => {
      const objBounds = obj.getBoundingRect();
      const isVisible = this.isObjectInViewport(objBounds, bounds);
      
      // Cache visibility state
      const objId = (obj as any).strokeId || obj.get('id') || '';
      const wasVisible = this.visibilityCache.get(objId) ?? true;
      
      if (isVisible !== wasVisible) {
        obj.visible = isVisible;
        this.visibilityCache.set(objId, isVisible);
        
        // Skip render calculations for invisible objects
        if (!isVisible) {
          (obj as any).skipOffscreen = true;
        } else {
          (obj as any).skipOffscreen = false;
        }
      }
    });
  }
  
  private isObjectInViewport(
    objBounds: { left: number; top: number; width: number; height: number },
    viewportBounds: { left: number; top: number; right: number; bottom: number }
  ): boolean {
    const objRight = objBounds.left + objBounds.width;
    const objBottom = objBounds.top + objBounds.height;
    
    return !(
      objBounds.left > viewportBounds.right ||
      objRight < viewportBounds.left ||
      objBounds.top > viewportBounds.bottom ||
      objBottom < viewportBounds.top
    );
  }
  
  // Enable/disable culling
  setEnabled(enabled: boolean) {
    this.cullingEnabled = enabled;
    
    if (!enabled) {
      // Make all objects visible
      this.canvas.getObjects().forEach(obj => {
        obj.visible = true;
        (obj as any).skipOffscreen = false;
      });
      this.visibilityCache.clear();
      this.canvas.requestRenderAll();
    }
  }
  
  // Get culling statistics
  getStats(): { total: number; visible: number; culled: number } {
    const objects = this.canvas.getObjects();
    const visible = objects.filter(obj => obj.visible).length;
    
    return {
      total: objects.length,
      visible,
      culled: objects.length - visible
    };
  }
  
  // Force visibility update
  forceUpdate() {
    this.updateObjectVisibility();
    this.canvas.requestRenderAll();
  }
  
  // Clear cache
  clearCache() {
    this.visibilityCache.clear();
  }
}